import { uuid, Response } from '../../../../../_metronic/helpers';
import {
  Concern,
  Diagnosis,
  EventLog,
  IInvoice,
  Language,
  Provider,
  Location,
  ServiceBundle,
} from '../../../../helpers/models';
import { UserModel } from '../../../auth';
import { AvailableTimeSlot } from '../../booking/core/_models';
import { ClientReferral } from '../../referrals/core/_models';
import { ISuperBillServiceLine, ProviderService, Service } from '../../services/core/_models';

export const hybrid_type_display_name: { [char: string]: string } = {
  R: 'Video',
  I: 'In-Person',
  P: 'Phone Call',
  H: 'Hybrid',
  N: 'No Preference', // legacy, never offered
  D: "Don't Care", // legacy
  U: 'Unknown', // legacy
};

/** The only values a selector offers and the only values new code writes (WST-762). */
export const SESSION_TYPE_OPTIONS = ['R', 'I', 'P', 'H'] as const;
export type SessionType = (typeof SESSION_TYPE_OPTIONS)[number];

/**
 * Mirror of the backend `effective_session_type`: fold a hybrid_type plus the
 * legacy phone/remote flags into one clean value. Prefer `session_type` from the
 * API when the record is saved; use this for unsaved form state.
 */
export const effectiveSessionType = (
  hybridType?: string | null,
  opts: { phoneMeeting?: boolean; remoteMeeting?: boolean; meetingLink?: string | null } = {}
): SessionType => {
  if (opts.phoneMeeting || hybridType === 'P') return 'P';
  if (hybridType === 'H') return 'H';
  if (opts.remoteMeeting || hybridType === 'R') return 'R';
  if (hybridType === 'I') return 'I';
  return opts.meetingLink ? 'R' : 'I';
};

export const client_status: { [char: string]: string } = {
  N: 'Newcomer',
  O: 'Onboarding',
  W: 'Waitlist',
  T: 'Trial',
  A: 'Active',
  F: 'Freeze',
  H: 'Hold',
  E: 'End of Services',
  R: 'Referral',
  V: 'Archived',
  C: 'Contact',
};

export interface Guardian {
  uuid: uuid;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  full_name?: string;
  name?: string;
  prefix?: string;
  suffix?: string;
  pronouns?: string;
  user?: UserModel;
  email?: string;
  phone?: string;
  timezone?: string;
  zipcode?: string;
  clients?: Array<Client>;
  avatar?: string;
  initials?: string;
  language?: string; // deprecated
  languages?: Array<Language>;
  other_languages?: string;
  address?: string;
  email_notifications?: boolean;
  text_notifications?: boolean;
  notifications?: any; // Granular notification settings
  primary_contact?: boolean;
  is_payer?: boolean;
  payment_email?: string;
  relationship?: string;
}

export interface SuperBillDiagnosis {
  uuid?: uuid;
  number: number;
  code: string;
  name: string;
}

export interface ClientTag {
  uuid: uuid;
  name: string;
  color: string;
}

export interface ClientProvider {
  uuid?: uuid;
  client?: Client;
  provider?: Provider;
  note?: string;
  meeting_link?: string;
  status?: string;
  hybrid_type?: string;
  phone_number?: string;
  session_type?: string;
  location?: string;
  start_date?: Date;
  makeup_count?: number;
  freeze_count?: number;
  session_credits_count?: number; // with specific provider
  balance_credits_count?: number; // with specific provider
  subscription_status_list?: any;
  superbill_additional_client_information?: string;
  superbill_diagnosis?: SuperBillDiagnosis[];
  location_obj?: Location;
  location_pos_code?: string;
  tags?: ClientTag[];
}

export interface ClientContactConsent {
  uuid?: uuid;
  consent_name?: string;
  consent_date?: Date;
  provider?: Provider;
  allow_org?: boolean;
  client_contact?: ClientContact;
}

export interface ClientContact {
  uuid?: uuid;
  client?: Client;
  contact?: Provider;
  consents?: Array<ClientContactConsent>;
  role?: string;
  is_editable?: boolean;
}

export const planTypes = {
  prepaid: 'Prepaid Fixed',
  'prepaid-schedule': 'Prepaid By Schedule',
  'postpaid-fixed': 'Postpaid Fixed',
  'postpaid-schedule': 'Postpaid By Schedule',
  'postpaid-metered': 'Postpaid By Attendance',
  manual: 'Manual',
};

/** WST-891: one service row on a plan (ClientSubscriptionServiceSerializer). */
export interface ClientSubscriptionService {
  uuid?: string | null; // null on the synthesized row of a legacy plan
  provider_service?: ProviderService;
  provider_service_uuid?: string; // write-only
  rate?: number | null;
  payout?: number | null;
  service_quantity?: number;
  service_interval_count?: number;
  position?: number;
  unit_cost?: number | null; // read-only
  fee_frequency?: 'one_time' | 'recurring' | null; // WST-681: fee rows only; null = the service's default
  is_fee?: boolean; // WST-681: read-only
}

export interface IClientInvoice {
  created: Date;
  period_start: Date;
  period_end: Date;
  due_date: Date;
  status: string;
  invoice_pdf: string;
  uuid: uuid;
  invoice_number: string;
  amount_due?: number;
  applied_balance?: number;
  total?: number;
  currency?: string;
  // WST-878: itemized invoicing — how the invoice was itemized against the
  // plan's per-cycle session quantity, and how many sessions it covers.
  itemize_state?: 'none' | 'reduced' | 'excess';
  itemized_session_count?: number;
  refunds?: Array<{
    amount: number;
    status: string;
    created: Date;
    reason: string;
  }>;
}

export interface ClientSubscription {
  uuid?: uuid;
  client?: Client;
  guardian?: Guardian;
  plan_type?: keyof typeof planTypes;
  status?: string;
  service_interval?: string;
  service_interval_count?: number;
  service_quantity?: number;
  start_date?: Date;
  end_date?: Date | string | null;
  provider_service?: ProviderService;
  provider?: Provider;
  rate?: number;
  currency?: string;
  cost?: number;
  cost_ach?: number;
  cost_not_ach?: number;
  unit_cost?: number;
  unit_cost_ach?: number;
  unit_cost_not_ach?: number;
  note?: string;
  service_fee_percent?: number;
  service_fee_ach_percent?: number;
  payment_setup_status?: string;
  invite?: ClientInvite;
  subscription_setup_complete?: boolean;
  subscription_scheduled?: boolean;
  subscription_interval?: string; // read only (backend)
  subscription_interval_count?: number; // read only (backend)
  subscription_quantity?: number; // read only (backend)
  slots_to_book?: number;
  invoices?: Array<IClientInvoice>;
  invoice_count?: number;
  created_at?: Date;
  pause_request_processing?: boolean;
  cancel_request_processing?: boolean;
  plan_end_date?: string | null;
  is_active?: boolean; // read only
  pauses?: Record<
    string,
    {
      start?: Date | string;
      end?: Date | string;
      provider_uuid?: string;
      provider_name?: string;
      cancel_sessions?: boolean;
      canceled_count?: number;
      cancel_credit?: 'session' | 'balance' | 'none' | string;
      scope?: 'subscription' | 'any' | string;
      reason?: string;
      type?: 'pause' | 'cancel' | string;
    }
  >;
  payout?: number | null; // WST-803: per-plan payout override (reports only)
  itemize_invoice?: boolean; // WST-878: bill by calendar sessions, capped at service_quantity
  notify_on_payment?: boolean; // WST-832: email the payer a receipt when an invoice is paid
  changes?: Array<{
    field: string;
    from?: unknown;
    to?: unknown;
    changed_at?: string;
    changed_by?: string | null;
    effective_from?: string | null;
  }>; // WST-803: read-only plan change log
  services?: Array<ClientSubscriptionService>; // WST-891: one row per service; legacy plans get one synthesized row
  provider_note?: string | null; // WST-891: provider/clinic-only; stripped for client users by the API
  is_tuition?: boolean; // read-only: fixed type with every quantity 0 - priced per period
  is_schedule?: boolean; // read-only: prepaid-schedule | postpaid-schedule
  is_per_session?: boolean; // read-only: schedule or by-attendance
  has_started?: boolean; // read-only (WST-950): billing started; start date is locked
  is_fee_only?: boolean; // WST-681: read-only; every row is a fee (also reads is_tuition: true)
  is_free?: boolean | null; // WST-681: read-only; false while any fee row bills; null on an interval mismatch
}

export interface ClientSubscriptionSlim {
  uuid?: uuid;
  plan_type?: keyof typeof planTypes;
  status?: string;
  service_interval?: string;
  service_interval_count?: number;
  service_quantity?: number;
  start_date?: Date;
  provider_service?: ProviderService;
  rate?: number;
  currency?: string;
  note?: string;
  subscription_setup_complete?: boolean;
  subscription_scheduled?: boolean;
  created_at?: Date;
  is_active?: boolean; // read only
}

export interface ILineItem {
  uuid?: uuid;
  description: string;
  qty: number;
  unit_rate: number;
  provider?: uuid;
  provider_service?: uuid;
  event_log?: uuid;
}

export interface ClientOneOffPayment {
  uuid?: uuid;
  /** WST-817: request-only opt-in; the clinic itself owns the bill */
  bill_to_clinic?: boolean;
  client?: Client;
  guardian?: Guardian;
  status?: string;
  provider_service?: ProviderService;
  service_bundle?: uuid | { uuid: uuid } | ServiceBundle;
  provider?: Provider;
  rate?: number;
  currency?: string;
  cost?: number;
  cost_ach?: number;
  cost_not_ach?: number;
  note?: string;
  service_fee_percent?: number;
  invite?: ClientInvite;
  is_free?: boolean; // read only
  created_at?: Date;
  status_date?: Date;
  payment_setup_complete?: boolean;
  add_session_credit?: number;
  invoice?: IInvoice;
  invoice_pdf?: string; // stripe invoice pdf link
  superbill_services?: Array<ISuperBillServiceLine>;

  line_items?: Array<ILineItem>;

  due_date?: Date;
  max_installments?: number;
  installment_count?: number;
  notify_on_payment?: boolean; // WST-832: email the payer a receipt when an invoice is paid
  installments?: {
    count: number;
    paid_count: number;
    next_due_date?: Date;
    next_amount_due?: number;
    invoices?: Array<
      IInvoice & {
        period_start?: Date;
        period_end?: Date;
        refunds?: Array<{
          amount: number;
          status: string;
          created: Date;
          reason: string;
        }>;
      }
    >;
  };
  amount_due?: number;
  applied_balance?: number;
  refunds?: Array<{
    amount: number;
    status: string;
    created: Date;
    reason: string;
  }>;

  // deprecated
  // use_paid_out_of_band?: boolean;
  // use_session_credit?: boolean;
  // use_payment_method_on_file?: boolean;
  // skip_payment?: boolean;

  // replaces all the 'use' nonsense
  payment_method?: string;

  discount_percent?: number;
  discount_amount?: number;

  discount_total?: number; // read only
  discount_total_ach?: number; // read only
  discount_total_not_ach?: number; // read only
}

export interface ClientOneOffPaymentSlim {
  uuid?: uuid;
  status?: string;
  provider_service?: ProviderService;
  rate?: number;
  currency?: string;
  note?: string;
  use_session_credit?: boolean;
  use_payment_method_on_file?: boolean;
  is_free?: boolean; // read only
  created_at?: Date;
  payment_setup_complete?: boolean;
  use_paid_out_of_band?: boolean;
}

export interface FormInfo {
  name: string;
  link?: string;
}

export interface Client {
  uuid?: uuid;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  full_name?: string;
  name?: string;
  prefix?: string;
  suffix?: string;
  pronouns?: string;
  user?: UserModel;
  email?: string;
  phone?: string;
  timezone?: string;
  zipcode?: string;
  guardians: Array<Guardian>;
  siblings?: Array<Client>;
  contacts?: Array<ClientContact>;
  avatar?: string;
  initials?: string;
  dob?: Date;
  age?: number;
  language?: string; // deprecated
  languages?: Array<Language>;
  other_languages?: string;
  address?: string;
  client_provider?: ClientProvider;
  client_subscriptions: Array<ClientSubscription>;
  one_offs?: Array<ClientOneOffPayment>;
  currency?: string;
  primary_guardian?: Guardian;
  email_notifications?: boolean;
  text_notifications?: boolean;
  notifications?: any; // Granular notification settings
  contact_emails?: [{ value: string; label: string }];
  primary_email?: string;
  primary_phone?: string;
  has_payment_method?: boolean;
  invoice_memo?: string;
  invoice_case_id?: string;
  concerns?: Array<Concern>;
  other_concerns?: string;
  diagnoses?: Array<Diagnosis>;
  other_diagnoses?: string;
  minor?: boolean;
  type?: string;
  forms?: Array<IWizForm>;
  is_payer?: boolean;
  session_credits_count?: number; // with all providers
  balance_credits_count?: number; // with all providers
  payment_email?: string;
  superbill_allowed?: boolean;
  classwallet_allowed?: boolean;
  insurance_details?: any;
}

export type ClientsQueryResponse = Response<Array<Client>>;
export type ClientProvidersQueryResponse = Response<Array<ClientProvider>>;
export type GuardiansQueryResponse = Response<Array<Guardian>>;
export type ClientSubscriptionQueryResponse = Response<Array<ClientSubscription>>;

export interface ClientActivityEntry {
  uuid?: uuid;
  created_at?: string;
  client?: uuid;
  provider?: uuid;
  provider_name?: string;
  created_by?: uuid;
  created_by_name?: string;
  category?: string;
  activity_type?: string;
  description?: string;
  time_spent_minutes?: number;
  details?: Record<string, any>;
  event_log?: uuid;
  wiz_invoice?: uuid;
  client_invite?: uuid;
  client_subscription?: uuid;
  client_oneoff?: uuid;
  invite_complete?: boolean | null;
}

export const activity_category_labels: Record<string, string> = {
  invite: 'Invites',
  bill: 'Bills',
  subscription: 'Service Plans',
  email: 'Emails',
  text: 'Texts',
  session: 'Sessions',
  note: 'Notes & Docs',
  manual: 'Manual Entries',
  credit: 'Credits',
  client: 'Client',
};

export const manual_activity_type_labels: Record<string, string> = {
  manual_phone_call: 'Phone Call',
  manual_email: 'Email',
  manual_text: 'Text',
  manual_admin_task: 'Admin Task',
  manual_other: 'Other',
};

export const activity_type_labels: Record<string, string> = {
  invite_sent: 'Invite Sent',
  invite_completed: 'Invite Completed',
  bill_sent: 'Bill Sent',
  bill_link_copied: 'Bill Link Copied',
  bill_paid: 'Bill Paid',
  bill_payment_failed: 'Bill Payment Failed',
  plan_created: 'Service Plan Created',
  plan_sent: 'Service Plan Sent',
  plan_activated: 'Service Plan Activated',
  plan_paused: 'Service Plan Paused',
  plan_canceled: 'Service Plan Canceled',
  email_sent: 'Email Sent',
  email_note_shared: 'Note Shared',
  email_document_shared: 'Document Shared',
  email_reminder: 'Reminder Email',
  text_sent: 'Text Sent',
  text_reminder: 'Reminder Text',
  note_created: 'Note Created',
  note_edited: 'Note Edited',
  note_shared: 'Note Shared',
  document_uploaded: 'Document Uploaded',
  document_shared: 'Document Shared',
  session_scheduled: 'Session Scheduled',
  session_attended: 'Session Attended',
  session_no_show: 'Session No Show',
  session_canceled: 'Session Canceled',
  session_late_cancel: 'Session Late Cancel',
  session_rescheduled: 'Session Rescheduled',
  guardian_added: 'Guardian Added',
  guardian_updated: 'Guardian Updated',
  contact_added: 'Contact Added',
  contact_updated: 'Contact Updated',
  credit_session_added: 'Session Credit Added',
  credit_balance_added: 'Balance Credit Added',
  manual_phone_call: 'Phone Call',
  manual_email: 'Email',
  manual_text: 'Text',
  manual_admin_task: 'Admin Task',
  manual_other: 'Other',
};

export interface IOnboardingInvoiceDetails {
  number?: string;
  amount?: number;
  uuid?: uuid;
  classwallet?: boolean;
}

export interface ClientOnboarding {
  uuid: uuid;
  subscription: ClientSubscription;
  oneoff: ClientOneOffPayment;
  referral: ClientReferral;
  provider: Provider;
  service: Service;
  provider_service: ProviderService;
  client: Client;
  guardian: Guardian;
  ck: string; // stripe client secret
  pk: string; // stripe publishable key
  pm: any; // hash with payment method details if available, otherwise null
  // WST-974: type of the payment method on file, sent only for on-file invites.
  on_file_pm_type?: 'card' | 'us_bank_account' | null;
  ns: string; // stripe next step
  md: string; // microdeposit link
  complete: Date;
  details: any;
  invoice_details?: IOnboardingInvoiceDetails;
  // WST-877: server-derived ClassWallet program state (code, e.g. 'AZ');
  // null/absent = show no ClassWallet option.
  classwallet_client_state?: string;
  session_credit_count?: number;
  slots_to_book?: number; // how many slots this invite must book (1 for a reschedule)
  hybrid_type?: string;
  meeting_link?: string;
  location?: string;
  phone_number?: string;
  // WST-762: server-resolved session type the client may not change (invite pick or remote-only service).
  locked_session_type?: string | null;
}

export interface ClientInvite {
  uuid: uuid;
  subscription?: ClientSubscription;
  oneoff?: ClientOneOffPayment;
  referral?: ClientReferral;
  provider?: Provider;
  service?: Service;
  client?: Client;
  guardian?: Guardian;
  complete?: Date;
  details?: any;
  link?: string;
  invite_email_sent?: Date;
  last_opened?: Date;
  created_at?: Date;
  invite_email?: string;
  form?: FormInfo;
  form_template?: IWizFormTemplate;
  required_action?: string;
  setup_payment?: boolean;
  event_log?: EventLog;
  wiz_invoice?: PaymentIssueInfo;
  // Report-only computed fields
  type?: string;
  status?: 'pending' | 'complete' | 'expired' | 'removed';
  hybrid_type?: string;
  meeting_link?: string;
  location?: string;
  phone_number?: string;
}

export type PaymentIssueInfo = {
  uuid: string;
  amount: number;
  failure_reason: string;
  failed_at: string;
  clinic_name: string;
  retry_url: string;
  update_method_url: string;
  // WST-846 — server-side gating for the Retry control.
  retry_allowed?: boolean;
  retry_blocked_reason?: string | null;
};

export interface ClientIntake {
  uuid?: uuid;
  provider?: Provider;
  client?: Client;
  guardian?: Guardian;
  complete?: Date;
  formData?: any;
}

export interface IWizFormTemplateExists {
  exists?: boolean;
}
export interface IWizFormTemplate {
  uuid: uuid;
  name: string;
  display_name: string;
  description: string;
  schema: any;
  ui_schema: any;
  wizard_mode: boolean;
  invite_id_required: boolean;
  followup_template_name?: string;
  followup_url?: string;
  submit_button_text?: string;
  force_invite_id?: string;
  payment_enabled?: boolean;
  has_draft?: boolean;
  draft_schema?: any;
  draft_ui_schema?: any;
  draft_display_name?: string;
  created_at?: string;
  ai_status?: '' | 'processing' | 'completed' | 'failed';
  ai_error_message?: string;
  is_starred?: boolean;
  // false for public-library templates the provider can send but not edit; absent on older backends.
  can_manage?: boolean;
  tags?: ClientTag[];
}
export interface IWizForm {
  uuid?: uuid;
  data?: any;
  form_template?: uuid;
  form_template_name?: string;
  form_template_display_name?: string;
  ui_component?: string;
  invite_id?: string;
  is_complete?: boolean;
  timezone?: string;
  complete_date?: Date;
  http_referrer?: string;
  url_params?: any;
  booked_slots?: Array<AvailableTimeSlot>;
  ip_address?: string; // read only
  pending_confirm?: boolean; // read only: saved, waits for the existing client's emailed confirm link
}

export interface IClientReferral {
  uuid?: uuid;
  client?: Client;
  from_provider?: Provider;
  to_provider?: Provider;
  created_at: Date;
  note: string;
  concerns?: Array<Concern>;
  diagnoses?: Array<Diagnosis>;
  client_authorization?: Date;
  transfer?: boolean;
}

export interface IClientSessionCredit {
  uuid?: uuid;
  /** WST-817: request-only opt-in; the clinic itself owns the credit */
  bill_to_clinic?: boolean;
  client?: Client;
  provider?: Provider;
  created_at?: Date;
  service?: Service;
  provider_service?: ProviderService;
  credit_date?: Date;
  credit_use_event?: Event;
  credit_use_date?: Date;
  note?: string;
  is_active?: boolean;
  source_session?: ICreditSourceSession | uuid | null;
}

/**
 * WST-817: the session a credit was given for. Read as this object; written as
 * the EventLog uuid (null clears, an absent key leaves it unchanged).
 */
export interface ICreditSourceSession {
  uuid?: uuid;
  start?: string | Date;
  end?: string | Date;
  status?: string;
  event?: { uuid?: uuid; summary?: string } | null;
  // set on the picker's session list (logs and occurrences), not on the credit's read shape
  provider_service?: ProviderService;
  providers?: Array<Provider>;
}

export interface IClientBalanceCredit {
  uuid?: uuid;
  client?: Client;
  provider?: Provider;
  created_at?: Date;
  service?: Service;
  provider_service?: ProviderService;
  credit_date?: Date;
  credit_applied_date?: Date;
  value?: number;
  currency?: string;
  note?: string;
  is_active?: boolean;
  split?: boolean;
  // WST-878: the credit currently sits on an active DRAFT (upcoming) invoice
  // and has not already been detached from it. Only these can be released with
  // detachClientBalanceCredit; credit_applied_date alone is also true of
  // credits consumed by an invoice that has already been charged.
  applied_to_upcoming?: boolean;
  source_session?: ICreditSourceSession | uuid | null;
}

/**
 * WST-878: the detach response is the serialized credit plus the draft
 * invoice's fresh totals. They ride along because releasing a credit frees
 * headroom that the recalculation may immediately fill with another available
 * credit, so the caller cannot derive the new total from the credit alone.
 */
export interface IBalanceCreditDetachResponse extends IClientBalanceCredit {
  invoice?: {
    uuid?: uuid;
    amount_due?: number;
    applied_balance?: number;
  };
}
